// src/components/common/TrendIndicator.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import { THEME_COLORS } from '../../utils/colorHelpers';

/**
 * TrendIndicator - Displays a visual indicator for trend direction
 * Used across performance metrics to show increasing/decreasing/neutral trends
 *
 * @component
 * @example
 * <TrendIndicator trend="increasing" />
 * <TrendIndicator trend="decreasing" size="small" />
 */
const TrendIndicator = ({ trend, size = '1rem', sx = {} }) => {
  const icons = {
    increasing: (
      <ArrowUpwardIcon
        sx={{
          color: THEME_COLORS.success,
          fontSize: size,
          ...sx
        }}
      />
    ),
    decreasing: (
      <ArrowDownwardIcon
        sx={{
          color: THEME_COLORS.error,
          fontSize: size,
          ...sx
        }}
      />
    ),
    neutral: (
      <RemoveIcon
        sx={{
          color: THEME_COLORS.warning,
          fontSize: size,
          ...sx
        }}
      />
    )
  };

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle'
      }}
    >
      {icons[trend] || icons.neutral}
    </Box>
  );
};

TrendIndicator.propTypes = {
  /** Trend direction: 'increasing', 'decreasing', or 'neutral' */
  trend: PropTypes.oneOf(['increasing', 'decreasing', 'neutral']).isRequired,
  /** Icon size (CSS font-size value) */
  size: PropTypes.string,
  /** Additional MUI sx prop styles */
  sx: PropTypes.object
};

export default TrendIndicator;
